import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    from,
    firstValueFrom,
    of,
    timer,
    race,
    Subject,
    merge
} from 'rxjs';
import {
    catchError,
    map,
    switchMap,
    tap,
    take,
    retry,
    retryWhen,
    delay,
    scan,
    filter
} from 'rxjs/operators';
import { Network, ConnectionStatus } from '@capacitor/network';
import { Get, HttpService } from '@rfx/ngx-http';
import { HttpClient } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { Store } from '@ngxs/store';
import { AuthState } from '@ion-caballus/core/state/auth.state';

export interface NetworkStatus {
    isOnline: boolean;
    connectionQuality: 'strong' | 'moderate' | 'weak' | 'unknown';
    lastCheckTime: Date;
    failedAttempts: number;
}

export interface NetworkStatusCheckResult {
    type: 'success' | 'error' | 'timeout';
    value?: boolean | any;
}

const NETWORK_CHECK_INTERVAL = 5000; // 5 seconds
const CONNECTION_TIMEOUT = 10000; // 10 seconds
const STRONG_THRESHOLD = 1000; // 1 second
const MODERATE_THRESHOLD = 3000; // 3 seconds
const WEAK_THRESHOLD = 5000; // 5 seconds
const MAX_RETRY_ATTEMPTS = 3;
const MAX_BACKOFF_DELAY = 30000; // 30 seconds

@Injectable({
    providedIn: 'root'
})
export class NetworkStatusService extends HttpService {
    private _networkStatus: BehaviorSubject<NetworkStatus> = new BehaviorSubject<NetworkStatus>({
        isOnline: true,
        connectionQuality: 'unknown',
        lastCheckTime: new Date(),
        failedAttempts: 0
    });
    private _shouldDelay: boolean = false;
    private _forceCheck: Subject<void> = new Subject<void>();

    constructor(
        public _http: HttpClient,
        private platform: Platform,
        private _store: Store
    ) {
        super(_http);
        this.initializeNetworkListeners();
        this.startPeriodicCheck();
        this.initializeAppStateListener();
    }

    private initializeAppStateListener(): void {
        this.platform.resume.subscribe(() => {
            this._shouldDelay = true;
            // Force a check after the delay
            timer(5000).subscribe(() => {
                this._shouldDelay = false;
                this._forceCheck.next();
            });
        });
    }

    @Get('/network/networkStatus')
    public getNetworkStatus(): Observable<boolean> {
        return of(null);
    }

    get networkStatus$(): Observable<NetworkStatus> {
        return this._networkStatus.asObservable().pipe(take(1));
    }

    get currentNetworkStatus(): NetworkStatus {
        return this._networkStatus.value;
    }

    get continuousNetworkStatus$(): Observable<NetworkStatus> {
        return this._networkStatus.asObservable();
    }

    private async initializeNetworkListeners(): Promise<void> {
        const status = await Network.getStatus();
        this.updateNetworkStatus(status);

        Network.addListener('networkStatusChange', status => {
            this.updateNetworkStatus(status);
            // Force an immediate check when network status changes
            this._forceCheck.next();
        });
    }

    private updateNetworkStatus(status: ConnectionStatus): void {
        const currentStatus = this._networkStatus.value;
        const newStatus: NetworkStatus = {
            ...currentStatus,
            isOnline: status.connected,
            lastCheckTime: new Date()
        };

        // Reset failed attempts if we're coming back online
        if (status.connected && !currentStatus.isOnline) {
            newStatus.failedAttempts = 0;
        }

        this._networkStatus.next(newStatus);
    }

    private startPeriodicCheck(): void {
        // Merge periodic timer with force check events
        merge(timer(1000, NETWORK_CHECK_INTERVAL), this._forceCheck)
            .pipe(tap(() => this.checkConnectionQuality()))
            .subscribe();
    }

    public manualNetworkStatusCheck(): void {
        this._forceCheck.next();
    }

    private calculateBackoffDelay(retryAttempt: number): number {
        // Exponential backoff with max delay
        const delay = Math.min(Math.pow(2, retryAttempt) * 1000, MAX_BACKOFF_DELAY);
        return delay;
    }

    private checkConnectionQuality(): void {
        const start = Date.now();

        of(this._shouldDelay)
            .pipe(
                take(1),
                switchMap(shouldDelay => (shouldDelay ? timer(5000) : of(null))),
                filter(() => this._store.selectSnapshot(selector => selector.auth.token)),
                switchMap(() => {
                    return race([
                        this.getNetworkStatus().pipe(
                            take(1),
                            map(result => ({ type: 'success' as const, value: result })),
                            catchError(error => {
                                if (error?.status === 403) {
                                    return of({ type: 'success' as const, value: true });
                                }
                                return of({ type: 'error' as const, value: error });
                            })
                        ),
                        timer(CONNECTION_TIMEOUT).pipe(
                            map(() => ({ type: 'timeout' as const, value: false }))
                        )
                    ]).pipe(
                        retryWhen(errors =>
                            errors.pipe(
                                scan(retryCount => retryCount + 1, 0),
                                map(retryCount => {
                                    if (retryCount >= MAX_RETRY_ATTEMPTS) {
                                        throw new Error('Max retry attempts reached');
                                    }
                                    return retryCount;
                                }),
                                map(retryCount => this.calculateBackoffDelay(retryCount))
                            )
                        )
                    );
                })
            )
            .subscribe(result => {
                const duration = Date.now() - start;
                const currentStatus = this._networkStatus.value;
                let newStatus: NetworkStatus;

                if (result.type === 'timeout' || result.type === 'error') {
                    newStatus = {
                        isOnline: false,
                        connectionQuality: 'weak',
                        lastCheckTime: new Date(),
                        failedAttempts: currentStatus.failedAttempts + 1
                    };
                } else {
                    newStatus = {
                        isOnline: true,
                        connectionQuality: this.determineConnectionQuality(duration),
                        lastCheckTime: new Date(),
                        failedAttempts: 0
                    };
                }

                this._networkStatus.next(newStatus);
            });
    }

    private determineConnectionQuality(duration: number): NetworkStatus['connectionQuality'] {
        if (duration < STRONG_THRESHOLD) {
            return 'strong';
        }
        if (duration < MODERATE_THRESHOLD) {
            return 'moderate';
        }
        if (duration < WEAK_THRESHOLD) {
            return 'weak';
        }
        return 'unknown';
    }
}
